import { FC, Ref } from 'react';
import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useDrag, useDrop } from 'react-dnd';
import { Photo } from '../../photo';
import { TextField } from '../../inputs';
import { ChainIcon, DragIcon, SponsorStarIcon } from '../../../icons';
import { useCustomRegister } from '../../../hooks';
import { CardDeleteButton } from '../../buttons';
import { EventSponsorsFormValidator } from '../../../models/dto';
import { Colors } from '../../../constants';
import { clsx } from '../../../utils';
import { Showable } from '../../utils';
import { avatarBox, avatarStyles, dragAndDropIconStyles, inputsBox, sponsorCardStyles } from './sponsor.styles';
import { ALLOWED_USER_AVATAR_MIME_TYPES } from '@sweatpals/shared';

export const DragAndDropSponsorCardType = 'sponsor-card';

interface DragItem {
	id: string;
	index: number;
}

export interface SponsorsModalProp {
	controlId: `sponsors.${number}`;
	id: string;
	index: number;
	canDrag?: boolean;
	disabled?: boolean;
	getPublicFileUrl: (fileId: string) => string;
	onRemove: (index: number) => void;
	onMove(index: number, nexIndex: number): void;

}

export const SponsorCard: FC<SponsorsModalProp> = props => {
	const { controlId, getPublicFileUrl, index, onRemove, id, disabled, canDrag, onMove } = props;

	const form = useFormContext<EventSponsorsFormValidator>();

	const { register } = useCustomRegister(form);

	const [{ opacity }, drag, preview] = useDrag(
		() => ({
			type: DragAndDropSponsorCardType,
			item: { id, index },
			canDrag: canDrag && !disabled,
			collect: monitor => ({
				opacity: monitor.isDragging() ? 0 : 1,
			}),
		}),
		[id, index]
	);
	const [, drop] = useDrop<DragItem>(
		() => ({
			accept: DragAndDropSponsorCardType,
			hover: item => {
				const { id: draggedId, index: draggedIndex } = item;

				if (draggedId !== id) {
					onMove(draggedIndex, index);
					item.index = index;
				}
			},
		}),
		[onMove, id, index]
	);

	const avatarId = form.watch(`${controlId}.avatarId`);

	return (
		<Box ref={(node: HTMLDivElement) => { drop(preview(node)) }}
				 sx={clsx(sponsorCardStyles, { opacity })}>
			{index !== 0 && (
				<CardDeleteButton
					onDelete={() => {
						onRemove(index);
					}}
				/>
			)}

			<Showable show={canDrag}>
				<Box ref={drag as unknown as Ref<unknown>} sx={clsx(dragAndDropIconStyles, { cursor: canDrag && !disabled ? 'pointer' : 'default' })}>
					<DragIcon />
				</Box>
			</Showable>

			<Box sx={avatarBox}>
				<Photo
					{...register(`${controlId}.avatarImg`, {
						passRef: false,
					})}
					previousValue={avatarId ? getPublicFileUrl(avatarId) : undefined}
					onChange={uploadFile => {
						form.setValue(`${controlId}.avatarImg`, uploadFile);

						if (uploadFile === null) {
							form.setValue(`${controlId}.avatarImg`, undefined);
						}
					}}
					accept={ALLOWED_USER_AVATAR_MIME_TYPES.reduce((acc, key) => ({ ...acc, [key]: [] }), {})}
					maxWidth={140}
					sx={avatarStyles}
					iconSx={{
						color: Colors.white,
					}}
				/>
			</Box>
			<Box sx={inputsBox}>
				<TextField
					{...register(`${controlId}.sponsorName`)}
					fullWidth
					placeholder="Sponsor's Name"
					startAdornment={<SponsorStarIcon />}
				/>
				<TextField
					{...register(`${controlId}.sponsorUrl`)}
					fullWidth
					placeholder='Link to website'
					startAdornment={<ChainIcon />}
				/>
			</Box>
		</Box>
	);
};
